/* eslint-disable react/jsx-pascal-case */
/* eslint-disable react/no-unstable-nested-components */
  import { useContext, useMemo, useState, useCallback, useEffect } from 'react';
  import { useMaterialReactTable, type MRT_ColumnDef, type MRT_ColumnFiltersState, type MRT_PaginationState, type MRT_SortingState, type MRT_RowSelectionState, MRT_TableOptions, MRT_TableInstance, MRT_ToggleFiltersButton, MRT_Cell, MRT_Row, LiteralUnion, MRT_TableContainer, MRT_TableHeadCellFilterContainer, MRT_Table, MRT_TableHead, MRT_TopToolbar, MRT_TableFooter, MRT_BottomToolbar, MRT_Updater } from 'material-react-table';
  import { Box, Button, Checkbox, MenuItem, Paper, Select, Stack, TextField, useMediaQuery } from '@mui/material';
  import { postMainAppApiData, getTaxReportsApiData, serializeTaxReportBody, getMainAppApiData } from 'services/api';
  import { useTranslation } from 'react-i18next';
  import { StoreContext } from 'models/import';
  import { ValidationDetail, ValidationTableDef, ExchangeValue, ValidationSelectOption, ValidationMaterialTableProps, GeneralTransactionType, GeneralTransactionTypeModel, generalTransactionTypes, ValidationEditItem, ValidationMaterialTableMatchProps, CoinValue, ExchangeValidationFilter } from 'models/validation';
  import Tooltip, { TooltipAlignEnum, TooltipCursorEnum, TooltipModifier } from 'components/tooltip';
  import { TableDataRes } from '../base/tableBaseApi';
  import { isTrade, getAccountBalance, getAccountBalanceAfter, getAccountBalanceByExchange, getAccountBalanceAfterByExchange, getTransactionType, getDateForValidation, getDateStringFromApi,getDateString, getExchanges, getBuy, getSell, getRowSelectionIds, getGeneralType, filterAllowedEditTransactionTypes, handleEditApiCall, getRowById, getSubRowItems, getCleanedSubRow, getStringValueForTrade, getFee, canExpand, getNotices, getSubRowPossibleMatches, getPossibleMatches, getRowId, cleanUpTableId, getTransactionId, getHourString, getDateForFilter, getTimestamp } from 'services/transactions';
  import ValidationStatusCell from 'pages/validation/components/validationStatusCell';
  import ValidationEditDeleteComponent, { ValidationEditDeleteComponentProps } from 'pages/validation/components/validationEditDelete';
  import BuySellComponent from 'pages/validation/components/buySell';
  import { getStripedRowColor, getStripedRowsForExpansion } from 'services/util';
  import { useGetValidationRequest } from 'pages/validation/network/validationRequest';
  import ContentCopyIcon from '@mui/icons-material/ContentCopy';
  import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
  import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
  import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
  import dayjs, { Dayjs } from 'dayjs';
  import ValidationDepositWithdrawalTableFilters, { ValidationDepositWithdrawalTableFilterValues } from '../filters/validationDepositWithdrawalTableFilters';
  import { getDefaultMRTOptions } from '../materialTable';
  import useStateLocalStorage from 'hooks/useStateLocalStorage';
import { useQuery, useQueryClient } from '@tanstack/react-query';

  export default function ValidationDepositWithdrawalTable(props: ValidationMaterialTableMatchProps) {
    const { t } = useTranslation();
    const { userSettings } = useContext(StoreContext);
    const queryClient = useQueryClient()

    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 10 });
    
    //The table doesn't support custom useState so we need to use both and keep them in sync
    const [columnFiltersLocalStorage, setColumnFiltersLocalStorage] = useStateLocalStorage<MRT_ColumnFiltersState>('review_column_filters_v3_'+(userSettings?.userId || Date.now()), []);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(columnFiltersLocalStorage);
    const [filters, setFilters] = useStateLocalStorage<ValidationDepositWithdrawalTableFilterValues>('review_filter_v3_'+(userSettings?.userId || Date.now()), 
      {currencies:0,match_same_exchange:true,amount_accuracy:90,time_accuracy:3,already_matched:false,exchanges:[]}
    );
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

    const setRowSelectionAndUpdateProps = (rowSelection: MRT_RowSelectionState) => {
      setRowSelection(rowSelection);
      props.setRowSelection(rowSelection);
    };

    //sync local storage with column filters, we can't set it directly since the table uses its own custom function for it
    useEffect(() => {
      setColumnFiltersLocalStorage(columnFilters);
    }, [columnFilters]);

    const {
      data, isError, isFetching: isFetchingMain, isLoading, refetch,
    } = useGetValidationRequest(
      "validationWithdrawalTable",
      "/reports/tax/validation/incomplete-transactions",
      props.setIsFetching,
      columnFilters, 
      props.globalSearch, 
      pagination.pageIndex, 
      pagination.pageSize, 
      sorting, 
      JSON.stringify(filters),
      (response: TableDataRes<ValidationTableDef, ValidationDetail> | null) => {
        props.onLoadCb(response);
    },
    true,
  );
    const {
      data: exchangeList,
      isFetching: isFetchingExchange,
    } = useQuery<string[]>({
      queryKey: [
        "exchangeItemsValidationFilter",
        userSettings?.userId,
      ],
      queryFn: async (item) => {

        const response = await getMainAppApiData<ExchangeValidationFilter>('/ajax/validation-report/exchanges.php')
        if (response.data) {
          const exchangeFilters = columnFilters.filter((e) => e.id === 'exchanges')
          if (response.data && exchangeFilters.length > 0 && response.data.length > 0) {
            const values = exchangeFilters[0].value as string[] || [];
            values.forEach((i) => {
              //item no longer present (ex user has deleted all tx for that exchange)
              if (response && response.data && !response.data.find((item) => item === i)) {
                //remove exchange from filters
                setColumnFilters(columnFilters.filter((e) => e.id !== 'exchanges'));
              }
            })
          }
          return response.data;
        } else {
          return [];
        }
      },
    });

  const getSelectedRowItems = useCallback(() => {
    const selectedRowIds = getRowSelectionIds(rowSelection);
    const selectedRows = (data?.data || []).filter((row: ValidationTableDef) => selectedRowIds.includes(row.transaction.id || -1));
    return selectedRows;
  }, [rowSelection, JSON.stringify(data?.data || [])]);

  // Edit
  const tableData = (data?.data || []);
  const [isSaving, setIsSaving] = useState(false);
  const isFetching = isFetchingMain || props.isFetching || isFetchingExchange || isSaving || isLoading;
  const isDeletebuttonDisabled = isFetching || Object.keys(rowSelection).length < 1;

  const handleSaveRowEdits: MRT_TableOptions<ValidationTableDef>['onEditingRowSave'] =
  // @ts-ignore
  async ({ exitEditingMode, row, values }) => {
    handleEdit(exitEditingMode, row.id, values);
  };

  const handleEdit = (
    exitEditingMode: () => void, 
    tableId: string,  
    values: Record<LiteralUnion<string>, any>) => {

    const timestamp = getTimestamp(values.transaction_time);

    const valueItem: ValidationEditItem = {
      transaction_type: values.transaction_type,
      comment: values.comment,
      exchange: values.exchange,
      buy: values.buy,
      sell: values.sell,
      buy_coin: values.buy_coin,
      sell_coin: values.sell_coin,
      sell_value_in_fiat: values.sell_value_in_fiat,
      buy_value_in_fiat: values.buy_value_in_fiat,
      trade_id: values.transaction_id,
      transaction_time: timestamp ? timestamp : undefined,
    };

    const id = cleanUpTableId(tableId);
    if (!id) {
      console.log("empty id for edit");
      exitEditingMode();
      return;
    }

    exitEditingMode();
    updateRows([id], valueItem, false);

    };

    const updateRows = (id: string[], row: ValidationEditItem, fromToolbar: boolean) => {
      setIsSaving(true);

      handleEditApiCall(id, row, () => {
        setIsSaving(false);
        refetch();
      },
      () => {
        setIsSaving(false);
        refetch();
      });
  };
  
    // Matches

    const resetExpansion = () => {
      if (table) {
        table.resetExpanded();
      }
    }
  
    const deleteMatch = (pairId: number | null, transationId: number, userId: number, coin: string, fromTime: number, toTime: number) => {
      props.setIsFetching(true);
      const queryString: string = serializeTaxReportBody({
        method: 'POST',
        path: '/reports/tax/validation/update-missing-pair',
        body: `{"user_id": ${userId}, "transaction_id": ${transationId}, "pair_id": ${pairId}, "strict_mode": ${isStrictMode() ? 'true' : 'false'}}`,
        header: 'Accept: application/json',
      });
  
      getTaxReportsApiData<any>(`/ajax/query.php?${queryString}`).then(() => {
        fillUnperfect(userId, coin, fromTime, toTime).then(() => {
          queryClient.invalidateQueries({ queryKey: ['validationWithdrawalTable'] });
          props.setIsFetching(false);
        }).catch((e) => {
          resetExpansion();
          props.setIsFetching(false);
          refetch();
        });
      }).catch((e) => {
        resetExpansion();
        props.setIsFetching(false);
        refetch();
      });
    };
  
    const updateMatch = (pairId: number, transationId: number, userId: number, coin: string, fromTime: number, toTime: number) => {
      props.setIsFetching(true);
      const queryString: string = serializeTaxReportBody({
        method: 'POST',
        path: '/reports/tax/validation/update-missing-pair',
        body: `{"user_id": ${userId}, "transaction_id": ${transationId}, "pair_id": ${pairId}, "strict_mode": ${isStrictMode() ? 'true' : 'false'}}`,
        header: 'Accept: application/json',
      });
  
      getTaxReportsApiData<any>(`/ajax/query.php?${queryString}`).then(() => {
        fillUnperfect(userId, coin, fromTime, toTime).then(() => {
          queryClient.invalidateQueries({ queryKey: ['validationWithdrawalTable'] });
          props.setIsFetching(false);
        }).catch((e) => {
          resetExpansion();
          props.setIsFetching(false);
          refetch();
        });
      }).catch((e) => {
        resetExpansion();
        props.setIsFetching(false);
        refetch();
      });
    };

    const forceMatch = async (pairId: number, transationId: number, deleteId: number, userId: number, coin: string, fromTime: number, toTime: number) => {
      const queryString: string = serializeTaxReportBody({
        method: 'POST',
        path: '/reports/tax/validation/update-missing-pair',
        body: `{"user_id": ${userId}, "transaction_id": null, "pair_id": ${deleteId}, "strict_mode": ${isStrictMode() ? 'true' : 'false'}}`,
        header: 'Accept: application/json',
      });
  
      await getTaxReportsApiData<any>(`/ajax/query.php?${queryString}`);
      updateMatch(pairId, transationId, userId, coin, fromTime, toTime);
    };

  const fillUnperfect = async (userId: number | null | undefined, coin: string | null, fromTime: number | null, toTime: number | null) => {
    if (!userId) return;
    props.setIsFetching(true);
    let body: string;
    if (coin && fromTime && toTime) {
      body = `{"user_id": ${userId}, "coin": "${coin}", "transaction_time_from": ${fromTime}, "transaction_time_to": ${toTime}, "strict_match_only": false}`
    } else {
      body = `{"user_id": ${userId}, "strict_match_only" : false}`
    }
    const queryString: string = serializeTaxReportBody({
      method: 'GET',
      path: '/reports/tax/validation/missing/fill-unperfect',
      body: body,
      header: 'Accept: application/json',
    });

    await getTaxReportsApiData<any>(`/ajax/query.php?${queryString}`);
    props.setIsFetching(false);
    resetExpansion();
    refetch();
  };

  const fillPerfect = async (userId: number | null | undefined) => {
    if (!userId) { return; }
    props.setIsFetching(true);
    const queryString: string = serializeTaxReportBody({
      method: 'GET',
      path: '/reports/tax/validation/missing/fill-perfect',
      body: `{"user_id": ${userId}}`,
      header: 'Accept: application/json',
    });

    await getTaxReportsApiData<any>(`/ajax/query.php?${queryString}`);
    props.setIsFetching(false);
  };

  const recalculateMatches = async (userId: number | null | undefined) => {
    await fillPerfect(userId);
    await fillUnperfect(userId, null, null, null);
  };

  const isStrictMode = (): boolean => {
    return filters.amount_accuracy == 100 && filters.time_accuracy == 1
  };


    const onDetailNavigate = (id: number | null): void => {
      if (!id) { return; }
      //We need to use jquery to fake a form submit with post
      $('<form method="POST" target="_blank" action="/enter_coins.php"><input type="hidden" name="edit_tx" value="'+id+'" /></form>').appendTo('body').submit().remove();
    };

    const columns = useMemo<MRT_ColumnDef<ValidationTableDef>[]>(
      () => [
        // Model is recursive 1 level, ignore recursion warning
        // @ts-ignore
        {
          id: 'transaction_type',
          accessorKey: 'transaction.transaction_type',
          enableColumnFilter: false,
          filterVariant: 'multi-select',
          filterSelectOptions: props.transactionTypeSelectOption.filter((e) => e.value === 1 || e.value === 2),
          enableEditing: true,
          Edit: (info) => {
            const type = info.row.original.transaction.transaction_type;
            if (!type) return null;
            const items = filterAllowedEditTransactionTypes(type, props.transactionTypeSelectOption);
            const defaultValue = type.toString() ?? "";

            return (
            <TextField
            id="outlined-select"
            variant="standard"
            select
            defaultValue={defaultValue}
            sx={{ width: '100%', top: '-3px' }}
            size='small'
            onChange={(event) => {
              info.row._valuesCache[info.column.id] = event.target.value;
            }}
            onBlur={() => {
              handleSaveCellRow(info.row.id, info.row._valuesCache);
              //sync changes since we can't use the cell on blur
              info.table.setEditingCell(null);
              info.table.setEditingRow(null);
            }}
          >
            {items.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </TextField>
          )},
          header: t('Type', { ns: 'validation' }),
          accessorFn: (row) => row.transaction.transaction_type,
          Cell: (info) => {
            const stringValue = getTransactionType(info.row.original.transaction.transaction_type, info.row.original, props.transactionTypeSelectOption);
            return (<span><b>{stringValue}</b></span>);
          },
        },
        {
          id: 'coin',
          accessorKey: 'transaction.sell',
          enableEditing: false,
          enableSorting: false,
          Edit: (info) => {
            const buy = info.row.original.transaction.buy;
            const buyCoin = info.row.original.transaction.buy_coin;

            const sell = info.row.original.transaction.sell;
            const sellCoin = info.row.original.transaction.sell_coin;

            const isBuy = buy && buy !== 0;
            const value = isBuy ? buy : sell;
            const coin = isBuy ? buyCoin : sellCoin;

            return (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                
            <TextField
            id="outlined-select"
            variant="standard"
            defaultValue={value}
            onChange={(event) => {
              info.row._valuesCache[isBuy ? "buy" : "sell"] = event.target.value;
            }}
          />
          <TextField
            id="outlined-select"
            variant="standard"
            defaultValue={coin}
            onChange={(event) => {
              info.row._valuesCache[isBuy ? "buy_coin" : "sell_coin"] = event.target.value;
            }}
          />
          </div>
          )},
          enableColumnFilter: true,
          header: t('Transaction', { ns: 'validation' }),
          muiFilterTextFieldProps: { placeholder: t('Filter by Coin', { ns: 'validation' }) },
          minSize: 180,
          accessorFn: (row) => getSell(
            row.transaction.sell,
            row.transaction.sell_coin,
            row.transaction.sell_value_in_fiat,
            row.transaction.exchange,
            row,
          ),
          Cell: (info) => {
            const sell = getSell(
              info.row.original.transaction.sell,
              info.row.original.transaction.sell_coin,
              info.row.original.transaction.sell_value_in_fiat,
              info.row.original.transaction.exchange,
              info.row.original,
            );

            const buy = getBuy(
              info.row.original.transaction.buy,
              info.row.original.transaction.buy_coin,
              info.row.original.transaction.buy_value_in_fiat,
              info.row.original.transaction.exchange,
              info.row.original,
            );

            const fee = getFee(info.row.original.transaction.fee, info.row.original.transaction.fee_coin, info.row.original);
    
            return (
              <Tooltip
                cursor={TooltipCursorEnum.HELP}
                align={TooltipAlignEnum.LEFT}
                tooltipChildren={(
                  <BuySellComponent
                  sell={sell}
                  buy={buy}
                  fee={fee}
                  fiatOnly={false}
                  />
                )}
                modifiers={[]}
                key={info.column.id}
              >
              <div
                className="ct_tableBase-text-wrap-one-line"
                style={{ maxWidth: '100%' }}
              >
                <BuySellComponent
                sell={sell}
                buy={buy}
                fee={fee}
                fiatOnly={false}
                />
              </div>
            </Tooltip>
            );
          },
        },
        {
          id: 'transaction_value_in_fiat',
          accessorKey: 'transaction.sell_value_in_fiat',
          enableColumnFilter: false,
          enableEditing: false,
          enableSorting: false,
          Edit: (info) => {
            const buyFiat = info.row.original.transaction.buy_value_in_fiat;
            const sellFiat = info.row.original.transaction.sell_value_in_fiat;

            return (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                
            <TextField
            id="outlined-select"
            variant="standard"
            defaultValue={buyFiat}
            onChange={(event) => {
              info.row._valuesCache["buy_value_in_fiat"] = event.target.value;
            }}
          />
          { '->' }
          <TextField
            id="outlined-select"
            variant="standard"
            defaultValue={sellFiat}
            onChange={(event) => {
              info.row._valuesCache["sell_value_in_fiat"] = event.target.value;
            }}
          />
          </div>
          )},
          header: t('Value in FIAT', { ns: 'validation' }),
          minSize: 100,
          accessorFn: (row) => getSell(
            row.transaction.sell,
            row.transaction.sell_coin,
            row.transaction.sell_value_in_fiat,
            row.transaction.exchange,
            row,
          ),
          Cell: (info) => {
            const sell = getSell(
              info.row.original.transaction.sell,
              info.row.original.transaction.sell_coin,
              info.row.original.transaction.sell_value_in_fiat,
              info.row.original.transaction.exchange,
              info.row.original,
            );

            const buy = getBuy(
              info.row.original.transaction.buy,
              info.row.original.transaction.buy_coin,
              info.row.original.transaction.buy_value_in_fiat,
              info.row.original.transaction.exchange,
              info.row.original,
            );
    
            return (
              <Tooltip
                cursor={TooltipCursorEnum.HELP}
                align={TooltipAlignEnum.LEFT}
                tooltipChildren={(
                  <BuySellComponent
                  sell={sell}
                  buy={buy}
                  fee={null}
                  fiatOnly={true}
                  />
                )}
                modifiers={[]}
                key={info.column.id}
              >
              <div
                className="ct_tableBase-text-wrap-one-line"
                style={{ maxWidth: '100%' }}
              >
                <BuySellComponent
                sell={sell}
                buy={buy}
                fee={null}
                fiatOnly={true}
                />
              </div>
            </Tooltip>
            );
          },
        },
        {
          id: 'transaction_id',
          accessorKey: 'transaction.trade_hash',
          header: t('TX ID', { ns: 'validation' }),
          enableColumnFilter: false,
          enableEditing: false,
          minSize: 100,
          accessorFn: (row) => getTransactionId(row.transaction.trade_hash, row.transaction.trade_id),
          Cell: (info) => {
            const value: string | null = getTransactionId(info.row.original.transaction.trade_hash, info.row.original.transaction.trade_id);
    
            if (!value) { return null; }

            return (
            <Tooltip
                cursor={TooltipCursorEnum.HELP}
                align={TooltipAlignEnum.LEFT}
                title={value || ''}
                modifiers={[]}
                key={info.column.id}
              >
              <div
                className="ct_tableBase-text-wrap-one-line"
                style={{ maxWidth: '500px' }}
              >
                  <ContentCopyIcon 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!value) return;
                    navigator.clipboard.writeText(value);
                  }}
                  sx={{ cursor: 'pointer', width: '15px', height: '15px', paddingRight: '4px', paddingTop: '4px' }}
                  />
                {value || ''}
              </div>
            </Tooltip>
          );
          },
        },
        {
          id: 'exchanges',
          accessorKey: 'transaction.exchange',
          header: t('Account', { ns: 'validation' }),
          enableColumnFilter: true,
          enableEditing: true,
          accessorFn: (row) => row.transaction.exchange,
          Filter: ({ header }) => {
            if (!exchangeList || !exchangeList.length || exchangeList.length < 1) return null;

            const getFilter: any = header.column.getFilterValue() || [];
            const setFilter = header.column.setFilterValue;

            return (
            <Select
              name="exchanges"
              id="outlined-select"
              variant="standard"
              multiple
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <>{t('Exchanges', { ns: 'validation' })}</>;
                }

                if (selected.length > 2) {
                  return `${selected.slice(0, 2).join(', ')} ,...`;
                } else {
                  return selected.join(', ');
                }
              }}
              sx={{ width: '100%' }}
              defaultValue={getFilter}
              label={t('Exchanges', { ns: 'validation' })}
              size='small'
              onChange={(event) => {
                console.log(event);
                if (event && event.target) {
                  setFilter(event.target.value);
                } else {
                  setFilter(null);
                }
              }}
              value={getFilter}
            >
              {exchangeList.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={getFilter.indexOf(option) > -1} />
                  {option}
                </MenuItem>
              ))}
            </Select>
            );
          },
          Edit: (info) => {
            const exchange = info.row.original.transaction.exchange;

            return (
              <>
          <TextField
            id="standard-basic"
            variant="standard"
            defaultValue={exchange}
            onChange={(event) => {
              if (!event) return;
              info.row._valuesCache["exchange"] =  event.target.value;;
            }}
            onBlur={() => {
              handleSaveCellRow(info.row.id, info.row._valuesCache);
            }}
            autoFocus
          />
          </>
          )},
          Cell: (info) => {
            const exchangeValue: ExchangeValue | null = getExchanges(
              info.row.original.transaction.exchange, 
              info.row.original
            );
            if (!exchangeValue) return null;
    
            return (
            <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ maxWidth: '155px' }}
            >
              {exchangeValue.exchange1 || ''}
              {exchangeValue.exchange1
                && exchangeValue.exchange2 && ' -> '}
              {exchangeValue.exchange2 || ''}
            </div>
          );
          },
        },
        {
          id: 'comment',
          accessorKey: 'transaction.comment',
          header: t('Comment', { ns: 'validation' }),
          accessorFn: (row) => row.transaction.comment,
          enableColumnFilter: false,
          enableEditing: true,
          enableSorting: false,
          Edit: (info) => {
            const comment = info.row.original.transaction.comment;

            return (
              <>
          <TextField
            id="standard-basic"
            variant="standard"
            defaultValue={comment}
            onChange={(event) => {
              if (!event) return;
              info.row._valuesCache["comment"] = event.target.value;
            }}
            onBlur={() => {
              handleSaveCellRow(info.row.id, info.row._valuesCache);
            }}
            autoFocus
          />
          </>
          )},
          Cell: (info) => {
            const value = getStringValueForTrade(
              info.row.original.transaction.comment,
              info.row.original
            );
    
            return (
              <Tooltip
                cursor={TooltipCursorEnum.HELP}
                align={TooltipAlignEnum.LEFT}
                title={value || ''}
                modifiers={[]}
                key={info.column.id}
              >
              <div
                className="ct_tableBase-text-wrap-one-line"
                style={{ maxWidth: '100px' }}
              >
                {value || ''}
              </div>
            </Tooltip>
          );
          },
        },
        {
        id: 'transaction_time',
        accessorKey: 'transaction.transaction_time',
        enableEditing: true,
        enableColumnFilter: true,
        enableColumnFilterModes: false,
        type: 'date',
        filterVariant: 'date',
        muiFilterDatePickerProps: {
          format: userSettings?.dateFormat === 'd.m.Y' ? 'DD.MM.YYYY' : 'MM/DD/YYYY',
          timezone: "UTC",
          views: ['year', 'month', 'day']
        },
         Filter: ( { header }) => {
          const getFilter: any = header.column.getFilterValue();
          const setFilter = header.column.setFilterValue;
          let getFilterFrom, getFilterTo, setFilterFrom, setFilterTo;

          if (getFilter && Array.isArray(getFilter) && getFilter.length === 2) {
            getFilterFrom = getFilter ? dayjs.utc(getFilter[0]) : null;
            getFilterTo = getFilter ? dayjs.utc(getFilter[1]) : null;
          } else {
            getFilterFrom =  null;
            getFilterTo = null;
          }

          setFilterFrom = (value: any) => {
              setFilter((prev: any) => {
                if (prev  && Array.isArray(prev)
                  && prev.length === 2) {
                    if (value) {
                      return [value, prev[1]];
                    } else {
                      return [undefined, prev[1]];
                  }
                } else {
                  return [value, undefined];
                }
              });
          };
          setFilterTo = (value: any) => {
              setFilter((prev: any) => {
                if (prev  && Array.isArray(prev)
                  && prev.length === 2) {
                    if (value) {
                      return [prev[0], value];
                    } else {
                      return [prev[0], undefined];
                  }
                } else {
                  return [undefined, value];
                }
              });
          };

          return (
            <div className='muiBox-root' style={{ display: 'grid', gap: '4px', gridTemplateRows: '1fr 1fr', minWidth: '130px' }}>
            <DatePicker
            format={userSettings?.dateFormat === 'd.m.Y' ? 'DD.MM.YYYY' : 'MM/DD/YYYY'}
            timezone="UTC"
            closeOnSelect={true}
            defaultValue={getFilterFrom}
            onChange={(event) => {
              if (event) {
                setFilterFrom(event.format());
              } else {
                setFilterFrom(null);
              }
            }}
            views={['year', 'month', 'day']}
            slotProps={{ textField: { variant: "standard" }, field: { clearable: true } }}
          />
          <DatePicker
            format={userSettings?.dateFormat === 'd.m.Y' ? 'DD.MM.YYYY' : 'MM/DD/YYYY'}
            timezone="UTC"
            closeOnSelect={true}
            defaultValue={getFilterTo}
            onChange={(event) => {
              if (event) {
                setFilterTo(event.format());
              } else {
                setFilterTo(null);
              }
            }}
            views={['year', 'month', 'day']}
            slotProps={{ textField: { variant: "standard" }, field: { clearable: true } }}
          />
          </div>
          );
        },
          Edit: (info) => {
            const time = info.row.original.transaction.transaction_time;
            let date: Date | null;
            if (time) {
              date = new Date(time * 1000);
            } else {
              date = null;
            }

            return (
              <>
          <DateTimePicker
            format={userSettings?.dateFormat === 'd.m.Y' ? 'DD.MM.YYYY HH:mm:ss' : 'MM/DD/YYYY HH:mm:ss'}
            timezone="UTC"
            closeOnSelect={true}
            defaultValue={dayjs.utc(date)}
            onChange={(event) => {
              console.log(event);
              if (!event) return;
              info.row._valuesCache["transaction_time"] =  event.format();
            }}
            views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
            slotProps={{ textField: { variant: "standard" } }}
            ampm={userSettings?.locale.toLowerCase().includes('us')}
            // @ts-ignore
            onBlur={() => {
              handleSaveCellRow(info.row.id, info.row._valuesCache);
              //sync changes since we can't use the cell on blur
              info.table.setEditingCell(null);
              info.table.setEditingRow(null);
            }}
            autoFocus
          />
          </>
          )},
          header: t('Date', { ns: 'validation' }),
          accessorFn: (row) => getDateForFilter(row.transaction.transaction_time, row),
          Cell: (info) => {
            const value: string | null = getDateForValidation(info.cell.row.original.transaction.transaction_time, userSettings?.dateFormat, info.cell.row.original);
            if (!value) return null;
            return (
            <div
              className="ct_tableBase-text-wrap-one-line"
              style={{ width: '130px' }}
            >
              {value}
            </div>
          );
          },
        },
        {
          id: 'status',
          size: 7,
          minSize: 7,
          maxSize: 7,
          header: t('Status', { ns: 'validation' }),
          enableEditing: false,
          enableSorting: false,
          enableColumnFilter: true,
          filterVariant: 'select',
          filterSelectOptions: [{
              text: t('All', {ns: 'validation'}),
              value: 0
            }, {
              text: t('Complete', {ns: 'validation'}),
              value: 1
          },{
              text: t('Incomplete', {ns: 'validation'}),
              value: 2
          }],
          muiFilterTextFieldProps: { 
            sx: { height: '23px', paddingLeft: '8px' },
          },
          muiMenuProps: { 
            sx: { height: '23px', padding: '0' },
          },
          accessorFn: (row) => getPossibleMatches(row.possible_matches, row.transaction.id),
          Cell: (info) => <ValidationStatusCell 
          cell={info.cell}
          handleDuplicatesButtonPress={() => {}}
          duplicatesCurrentlyHidden={true}
          warningsCurrentlyHidden={true}
          noticesCurrentlyHidden={false}
          isTrade={isTrade}
          deleteMatch={deleteMatch}
          updateMatch={updateMatch}
          forceMatch={forceMatch}
          getSubRowPossibleMatches={getSubRowPossibleMatches}
          isMatchIconOnly={true}
          showDetailButton={true}
          handleDetailClick={() => {
           onDetailNavigate(info.cell.row.original.transaction.id);
          }}
        />,
        },
      ],
    [exchangeList], // deps for reload
  );

  const handleSaveCell = (cell: MRT_Cell<ValidationTableDef>, value: any) => {

    //if using flat data and simple accessorKeys/ids, you can just do a simple assignment here
    tableData[cell.row.index][cell.column.id as keyof ValidationTableDef] = value;

    const values = tableData[cell.row.index];
    handleEdit(
      () => {},
      cell.row.id,
      values
    );
  };

  const handleSaveCellRow = (rowId: string, values: Record<string, any>) => {
      handleEdit(
        () => {},
        rowId,
        values
      );
    };

  const defaultMRTOptions = getDefaultMRTOptions<ValidationTableDef>();

  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns,
    data: tableData,
    initialState: {
      ...defaultMRTOptions.initialState,
      showColumnFilters: true,
      columnVisibility: {
        'mrt-row-expand': false,
        'mrt-row-select': true,
        'mrt-row-actions': true,
        before_balance: false,
        after_balance: false,
        before_balance_by_exchange: false,
        after_balance_by_exchange: false,
      },
    },
    enableSorting: true,
    muiEditTextFieldProps: ({cell}) => ({
      // onBlur is more efficient, but could use onChange instead
      onBlur: (event) => {
        handleSaveCell(cell, event.target.value);
      },
    }),
    muiTableBodyRowProps: (props) => ({
      // @ts-ignore
      ...defaultMRTOptions.muiTableBodyRowProps(props),
      height: '35px',
        sx: { 
          cursor: (!isFetching && canExpand(props.row.original)) ? 'pointer' : 'default',
          '& td': {
            backgroundColor: props.row.depth === 0 ? 'inherit' : getStripedRowColor()
          },
        },
    }),
    muiToolbarAlertBannerProps: isError ? {
      color: 'error',
      children: t('Error loading data'),
    } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: (e) => {
      setRowSelectionAndUpdateProps({});
      setPagination(e);
    },
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => {
      // @ts-ignore
      setRowSelectionAndUpdateProps((prev) =>
        updater instanceof Function ? updater(prev) : updater,
      );
    },
    rowCount: data?.meta?.total_item_count ?? 0,
    defaultColumn: {
      ...defaultMRTOptions.defaultColumn,
      minSize:10,
      size: 20,
    },
    state: {
      ...defaultMRTOptions.state,
        columnFilters: columnFilters,
        globalFilter: props.globalSearch,
        isLoading,
        pagination,
        showAlertBanner: isError,
        showSkeletons: isFetchingMain || isSaving || isLoading,
        sorting,
        rowSelection: rowSelection,
    },
    muiExpandButtonProps: ({
      row,
    }) => ({
      sx: {
        display: (canExpand(row.original)) ? 'flex' : 'none',
      },
    }),
    muiFilterTextFieldProps: {
      sx: { m: '0.5rem 0', width: '100%' },
    },
    muiTableHeadCellProps: () => ({
      sx: {
        padding: '2px',
      }
    }),
    muiTableBodyCellProps: ({cell, column, table}) => ({
      sx: {
        padding: '2px',
      },
      onClick: (e) => {
        if (isFetching) return;
        e.stopPropagation();

        const isEditable = column.columnDef.enableEditing
        if (isEditable) {
          table.setEditingCell(cell);
          e.stopPropagation();
        } else {
          table.setEditingCell(null);
          if (!canExpand(cell.row.original)) return;
          cell.row.toggleExpanded();
        }
      },
      onBlur: async (event: React.FocusEvent) => {
        //Changing the blur behaviour for the date field breaks it
        if (column.id === 'transaction_time' || column.id === 'transaction_type') {
          return;
        }
        event.preventDefault();
        table.setEditingRow(null);
        table.setEditingCell(null);      
      },
    }),
    getSubRows: (row) => getSubRowItems(row) ,
    getRowId: (row, index, parent) => {
      if (parent) {
        return getRowId(row, index, parent.original, parent.index) 
      } else {
        return getRowId(row, index) 
      }
    },
    renderTopToolbarCustomActions: () => (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
          { isDeletebuttonDisabled && (
            <Button
              color="error"
              disabled={isFetching || isSaving || isLoading}
              onClick={() => {
                recalculateMatches(userSettings?.userId);
              }}
              variant="contained"
              sx={{ fontSize: '12px' }}
            >
              {t('Check Matches', { ns: 'validation' })}
              {!!(data?.info?.match_cache_date && getDateForValidation(data?.info?.match_cache_date, userSettings?.dateFormat, null)) && (
                <span
                  style={{ textTransform: 'capitalize', fontSize: '10px', position: 'relative', left: '4px' }}
                >
                  {'('}
                  {t('Cached on', { ns: 'validation' })}
                  {' '}
                  {getDateForValidation(data?.info?.match_cache_date, userSettings?.dateFormat, null)}
                  {')'}
                </span>
              )}
            </Button>
          )}
          <ValidationEditDeleteComponent 
            isDeletebuttonDisabled={isDeletebuttonDisabled}
            isEditEnabled={true}
            isFetching={isFetching}
            statusSelectOption={props.statusSelectOption}
            transactionTypeSelectOption={props.transactionTypeSelectOption}
            rowSelection={rowSelection}
            setRowSelection={setRowSelectionAndUpdateProps}
            rowItems={getSelectedRowItems()}
            updateRows={updateRows}
          />
        </div>
    ),
    renderToolbarInternalActions: ({table}) => (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <ValidationDepositWithdrawalTableFilters 
          filters={filters}
          onFitlerCB={(e) => setFilters(e)}
          disabled={isFetching || isSaving || isLoading}
          tooltipAlignment={TooltipAlignEnum.RIGHT}
          isHidden={false}
        />
      </div>
    ),
    renderToolbarAlertBannerContent: () => (
      <p/>
    ),
    onEditingRowSave: handleSaveRowEdits,
    enableEditing: (row) => !isTrade(row.original) && (row && row.depth === 0),
    enableRowSelection: (row) => !isTrade(row.original) && (row && row.depth === 0),
    enableExpanding: true,
    enableColumnFilters: true,
    autoResetExpanded: true,
    // Hide the filter icons
    icons: {
      FilterAltIcon: () => <p />,
    },
  });

  return (
    <>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <div className='ct_transactionTable'>
      <MRT_TopToolbar table={table} />
      <div className='ct_transactionTableMain'>
        <MRT_TableContainer table={table} />
      </div>
      <MRT_BottomToolbar table={table} />
    </div>
    </LocalizationProvider>
    </>
  );
}